import { bind } from "@react-rxjs/core"
import { catchError, map, of, scan } from "rxjs"

import type { EquityPosition, PositionResult } from "@/generated/TradingGateway"
import { ERROR_POSITION_RESULT, UpdateType } from "@/generated/TradingGateway"

import { PositionService } from "../TradingGateway"

function positionReducer(
  positions: EquityPosition[],
  result: PositionResult,
): EquityPosition[] {
  if (result.type === ERROR_POSITION_RESULT)
    throw new Error("Error received from PositionService")
  const { updateType, payload } = result.payload
  switch (updateType) {
    case UpdateType.StateOfTheWorld:
    case UpdateType.Added:
      return [...positions, payload]
    case UpdateType.Updated:
      return positions.map((p) => (p.assetId === payload.assetId ? payload : p))
    case UpdateType.Deleted:
      return positions.filter((p) => p.assetId !== payload.assetId)
    default:
      return positions
  }
}

export const [usePositions, positions$] = bind<EquityPosition[]>(
  PositionService.getPositions().pipe(
    scan(positionReducer, []),
    catchError((err) => {
      const errorMsg = "Failed to load positions."
      console.error(errorMsg, err)
      return of([] as EquityPosition[])
    }),
  ),
  [],
)

export const [usePositionsValue] = bind<number>(
  positions$.pipe(
    map((positions) =>
      positions.reduce(
        (totalValue, position) => totalValue + Number(position.marketValue),
        0,
      ),
    ),
  ),
)

export type ProfitAndLossType = "total" | "daily"

export const [useProfitAndLoss] = bind((type: ProfitAndLossType) =>
  positions$.pipe(
    map((positions) => {
      const { pnlSum, weightedPnLChangeSum, totalQuantity } = positions.reduce(
        (
          acc,
          {
            unrealizedPl,
            unrealizedPlpc,
            unrealizedIntradayPl,
            unrealizedIntradayPlpc,
            qty,
          },
        ) => {
          const pnl = Number(
            type === "total" ? unrealizedPl : unrealizedIntradayPl,
          )
          const pnlPercent = Number(
            type === "total" ? unrealizedPlpc : unrealizedIntradayPlpc,
          )

          return {
            pnlSum: acc.pnlSum + pnl,
            weightedPnLChangeSum:
              acc.weightedPnLChangeSum + pnlPercent * Number(qty),
            totalQuantity: acc.totalQuantity + Number(qty),
          }
        },
        { pnlSum: 0, weightedPnLChangeSum: 0, totalQuantity: 0 },
      )

      const pnlPercent =
        totalQuantity > 0 ? weightedPnLChangeSum / totalQuantity : 0

      return {
        pnl: pnlSum,
        pnlPercent,
      }
    }),
  ),
)
